import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  BookCheck,
  BookMarked,
  Brain,
  FileText,
  Layers3,
  Loader2,
  type LucideIcon,
  Plus,
  Trash2,
  Video,
  WalletCards,
} from 'lucide-react';
import { notify } from '@/lib/notify';
import { Button } from '@ai-tutor/ui';
import { type LessonSettings, type NamedFlow } from '@/lib/api-client';
import {
  useUpdateUserLessonSettings,
  useUserLessonSettings,
} from '@/hooks/account/useUserLessonSettings';
import { FlowSelector } from '@/components/features/learning/content/prompt/FlowSelector';
import { isFeatureEnabled } from '@/config/app';
import { cn } from '@/lib/utils';

/**
 * Per-user lesson flow settings - rendered inside the "Lesson Flow"
 * card on the Learning style settings tab. The outer card already owns
 * the title + description, so this body just exposes the two controls:
 *
 *   1. **Default flow** - what gets used when /lessons/generate is called
 *      without an explicit `settings` payload.
 *   2. **Saved templates** - named NamedFlow rows the user can pick by
 *      name from the FlowSelector dropdown elsewhere.
 *
 * Backend: `GET /api/v1/users/me/lesson-settings` + `PUT` of the same.
 * Save bundles both fields into one PUT so the user only sees one Save
 * button instead of competing "Save default" / "Save flows" pairs.
 */

type StepKey =
  | 'text'
  | 'mindmap'
  | 'quiz'
  | 'flashcards'
  | 'video'
  | 'glossary'
  | 'highlights';

interface StepMeta {
  label: string;
  icon: LucideIcon;
}

// Icons match the canonical lesson-page artifact iconography:
//   text       → MediaViewer (FileText)
//   mindmap    → MediaViewer (Brain)
//   quiz       → PracticeViewer (BookCheck)
//   flashcards → PracticeViewer (WalletCards)
//   video      → MediaViewer (Video)
//   glossary   → ReferenceViewer (BookMarked)
//   highlights → MediaViewer (Layers3)
// Keeping the icon set in sync means the saved-template pill chips
// read as the same thing a user sees on the lesson page itself.
const STEP_META: Record<StepKey, StepMeta> = {
  text: { label: 'Text', icon: FileText },
  mindmap: { label: 'Overview', icon: Brain },
  quiz: { label: 'Quiz', icon: BookCheck },
  flashcards: { label: 'Flashcards', icon: WalletCards },
  video: { label: 'Video', icon: Video },
  glossary: { label: 'Glossary', icon: BookMarked },
  highlights: { label: 'Highlights', icon: Layers3 },
};

const ALL_STEPS: readonly StepKey[] = [
  'text',
  'mindmap',
  'quiz',
  'flashcards',
  'video',
  'glossary',
  'highlights',
] as const;

// While video generation is gated off (VITE_ENABLE_VIDEO), drop `video` from
// the selectable step chips so users can't add it to a saved custom flow.
// STEP_META keeps its `video` entry for any legacy saved flow that still
// lists it - the backend strips `video` at resolve time regardless.
// The mindmap is not a choice: it is the lesson plan, built first in every
// flow (backend `PLANNED_STEPS`), so it has no chip. Nor is the podcast: it
// is made per section, on demand, from the lesson itself.
const KNOWN_STEPS: readonly StepKey[] = ALL_STEPS.filter(
  step => step !== 'mindmap' && (step !== 'video' || isFeatureEnabled('video'))
);

/**
 * The part of `default_lesson_settings` this card owns, in a comparable form:
 * everything except `options.depth`, which the Lesson Depth card above edits
 * in the same document, and with `custom_steps` only on a custom flow. The
 * server answers `custom_steps: null` for a built-in flow while the selector
 * writes `undefined`, so re-picking the stored flow used to arm Save.
 */
function withoutDepth(settings: LessonSettings): LessonSettings {
  const options = { ...(settings.options ?? {}) };
  delete options.depth;
  const { custom_steps, ...rest } = settings;
  return {
    ...rest,
    ...(settings.flow === 'custom' ? { custom_steps } : {}),
    options,
  };
}

export function LessonFlowSection() {
  const { data, isLoading } = useUserLessonSettings();

  // Local mirrors so the user can edit before hitting Save. Server response
  // overwrites these on load and after a successful PUT.
  const [defaultSettings, setDefaultSettings] = useState<LessonSettings>({
    flow: 'core',
  });
  const [savedFlows, setSavedFlows] = useState<NamedFlow[]>([]);

  // Re-hydrate each draft only when ITS server value changes. Keying on the
  // whole `data` object reset an unsaved template every time the Lesson Depth
  // card saved, because that save lands in the same query.
  const serverFlow = data
    ? JSON.stringify(withoutDepth(data.default_lesson_settings))
    : undefined;
  const serverTemplates = data
    ? JSON.stringify(data.saved_custom_flows)
    : undefined;

  useEffect(() => {
    if (serverFlow !== undefined) setDefaultSettings(JSON.parse(serverFlow));
  }, [serverFlow]);

  useEffect(() => {
    if (serverTemplates !== undefined) {
      setSavedFlows(JSON.parse(serverTemplates));
    }
  }, [serverTemplates]);

  // Single combined Save. Both `default_lesson_settings` and
  // `saved_custom_flows` get PUT in one call so the user doesn't have
  // to think about which button saves what. The depth travels at its SERVER
  // value — the PUT replaces the document whole, and this card's draft does
  // not track depth.
  const updateSettings = useUpdateUserLessonSettings();
  const saveAll = useMutation({
    mutationFn: () => {
      const depth = data?.default_lesson_settings.options?.depth;
      return updateSettings.mutateAsync({
        default_lesson_settings: {
          ...defaultSettings,
          options: {
            ...(defaultSettings.options ?? {}),
            ...(depth ? { depth } : {}),
          },
        },
        saved_custom_flows: savedFlows,
      });
    },
    onSuccess: () => notify.success('Flow settings saved'),
    // `err` itself, not `err.message` — see LearningStyleSection's depth save.
    onError: (err: unknown) => {
      notify.error(err, "Couldn't save your flow settings");
    },
  });

  // JSON.stringify is fine for these small shapes and avoids a custom
  // deep-equal. Disables Save when nothing has changed.
  const isDirty = useMemo(() => {
    if (serverFlow === undefined) return false;
    return (
      JSON.stringify(withoutDepth(defaultSettings)) !== serverFlow ||
      JSON.stringify(savedFlows) !== serverTemplates
    );
  }, [serverFlow, serverTemplates, defaultSettings, savedFlows]);

  // Block save when any template is incomplete - empty name or zero
  // steps. The previous fall-back-to-Core behaviour was confusing;
  // forcing a valid shape at save time keeps the saved_custom_flows
  // list meaningful.
  const isValid = useMemo(
    () =>
      savedFlows.every(
        flow =>
          flow.name.trim().length > 0 &&
          flow.steps.some(step => step !== 'mindmap')
      ),
    [savedFlows]
  );

  if (isLoading) {
    return (
      <div className='flex h-40 items-center justify-center'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Default flow ---------------------------------------------------- */}
      <div>
        <h4 className='mb-2 text-xs font-medium capitalize text-muted-foreground'>
          Default flow
        </h4>
        <FlowSelector
          value={defaultSettings}
          onChange={setDefaultSettings}
          placement='bottom-start'
          savedFlows={savedFlows}
        />
      </div>

      {/* Saved templates ------------------------------------------------- */}
      <div>
        <div className='mb-2 flex items-center justify-between'>
          <h4 className='text-xs font-medium capitalize text-muted-foreground'>
            Saved templates
          </h4>
          <Button
            size='sm'
            variant='ghost'
            className='tap-target'
            onClick={() =>
              setSavedFlows(prev => [
                ...prev,
                { name: `My flow ${prev.length + 1}`, steps: ['text'] },
              ])
            }
          >
            <Plus className='h-3.5 w-3.5' />
            Add
          </Button>
        </div>

        <ul className='space-y-2.5'>
          {savedFlows.map((flow, idx) => (
            // Key must NOT include `flow.name` - every keystroke mutates
            // the name, which would change the key, which would unmount
            // and remount the input on each character (cursor jumps,
            // focus lost). Positional index is stable across edits and
            // correctly rebinds across add/remove operations.
            <SavedFlowRow
              key={idx}
              flow={flow}
              onChange={updated =>
                setSavedFlows(prev =>
                  prev.map((f, i) => (i === idx ? updated : f))
                )
              }
              onRemove={() =>
                setSavedFlows(prev => prev.filter((_, i) => i !== idx))
              }
            />
          ))}

          {savedFlows.length === 0 && (
            <li className='rounded-lg border border-dashed border-border bg-gray-50/50 px-4 py-6 text-center text-xs text-muted-foreground dark:bg-white/[0.03]'>
              No saved templates yet. Create one to reuse the same set of
              artifacts across lessons.
            </li>
          )}
        </ul>
      </div>

      {/* Save ------------------------------------------------------------ */}
      <div className='mt-5'>
        <Button
          size='sm'
          onClick={() => saveAll.mutate()}
          disabled={!isDirty || !isValid || saveAll.isPending}
        >
          {saveAll.isPending ? (
            <>
              <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SavedFlowRow({
  flow,
  onChange,
  onRemove,
}: {
  flow: NamedFlow;
  onChange: (next: NamedFlow) => void;
  onRemove: () => void;
}) {
  const toggleStep = (step: StepKey) => {
    // A flow saved before the mindmap was always on may still list it.
    const set = new Set(flow.steps.filter(s => s !== 'mindmap'));
    if (set.has(step)) set.delete(step);
    else set.add(step);
    onChange({ ...flow, steps: Array.from(set) });
  };

  const nameEmpty = flow.name.trim().length === 0;
  const noSteps = flow.steps.filter(s => s !== 'mindmap').length === 0;

  return (
    <li className='space-y-3'>
      <div className='flex items-center gap-2'>
        <input
          type='text'
          value={flow.name}
          onChange={e => onChange({ ...flow, name: e.target.value })}
          maxLength={60}
          placeholder='Flow name'
          aria-invalid={nameEmpty}
          className={cn(
            'flex-1 rounded-md border bg-card px-3 py-1.5 text-base font-medium text-foreground placeholder:text-muted-foreground/60 focus:outline-none max-sm:min-h-11 sm:text-sm',
            nameEmpty
              ? 'border-red-300 dark:border-red-800/60'
              : 'border-border'
          )}
        />
        <button
          type='button'
          onClick={onRemove}
          aria-label='Remove template'
          className='inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 dark:hover:text-red-300 max-sm:h-11 max-sm:w-11'
        >
          <Trash2 className='h-3.5 w-3.5' />
        </button>
      </div>

      {nameEmpty && (
        <p className='text-xs text-red-700 dark:text-red-300'>
          Template name cannot be empty.
        </p>
      )}

      {/* Below `sm` the chips are 36px with 44px tap areas, and the gap is
          8px - the two 4px overhangs - so wrapped rows never overlap. */}
      <div className='flex flex-wrap gap-1.5 max-sm:gap-2'>
        {KNOWN_STEPS.map(step => {
          const meta = STEP_META[step];
          const checked = (flow.steps as StepKey[]).includes(step);
          const Icon = meta.icon;
          return (
            <button
              key={step}
              type='button'
              // The chip's inclusion was shown by colour alone; announce it.
              aria-pressed={checked}
              onClick={() => toggleStep(step)}
              className={cn(
                'tap-target inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors max-sm:min-h-9 max-sm:px-3 max-sm:text-sm',
                checked
                  ? 'dark:tint-brand border-brand-300 bg-brand-50/70 text-brand-700 dark:border-primary/60'
                  : 'border-border bg-card text-muted-foreground hover:border-gray-300 hover:bg-accent dark:hover:border-gray-600'
              )}
            >
              <Icon className='h-3 w-3' />
              {meta.label}
            </button>
          );
        })}
      </div>

      <p className='text-xs text-muted-foreground'>
        Every lesson starts with its mindmap.
      </p>

      {noSteps && (
        <p className='text-xs text-red-700 dark:text-red-300'>
          Pick at least one step to save this template.
        </p>
      )}
    </li>
  );
}
